import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  StarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import {
  securityVendorService,
  type CoverageMatrix,
  type CoverageMatrixCell,
} from '@/services/securityVendor.service';
import { DOMAIN_LABELS, REGION_LABELS } from '@/components/security-vendors/vendorOptions';

/** Look up a cell by (region, domain). The matrix always contains the full grid. */
function cellAt(matrix: CoverageMatrix, region: string, domain: string): CoverageMatrixCell | undefined {
  return matrix.cells.find((c) => c.region === region && c.domain === domain);
}

export default function SecurityVendorCoverageMatrix() {
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  const { data: matrix, isLoading, isError, error } = useQuery({
    queryKey: ['security-vendor-coverage-matrix'],
    queryFn: () => securityVendorService.getCoverageMatrix(),
  });

  const handleExport = async (format: 'csv' | 'pdf') => {
    setExporting(format);
    try {
      await securityVendorService.exportCoverageMatrix(format);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/security/vendor-directory"
            className="mb-1 inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-700 dark:text-secondary-400"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Vendor Directory
          </Link>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white sm:text-3xl">
            Regional Coverage Matrix
          </h1>
          <p className="mt-1 text-secondary-500 dark:text-secondary-400">
            Recommended vendors by region and security domain — gaps highlighted.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            disabled={!matrix || exporting !== null}
            loading={exporting === 'csv'}
            onClick={() => void handleExport('csv')}
          >
            CSV
          </Button>
          <Button
            variant="secondary"
            leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            disabled={!matrix || exporting !== null}
            loading={exporting === 'pdf'}
            onClick={() => void handleExport('pdf')}
          >
            PDF
          </Button>
        </div>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="p-6 text-secondary-500 dark:text-secondary-400">Loading coverage matrix…</CardContent>
        </Card>
      )}

      {isError && (
        <Card>
          <CardContent className="p-6 text-danger-600 dark:text-danger-400">
            {error instanceof Error ? error.message : 'Failed to load coverage matrix.'}
          </CardContent>
        </Card>
      )}

      {matrix && (
        <>
          <div
            className="flex flex-wrap items-center gap-4 rounded-lg border border-secondary-200 bg-white p-4 text-sm dark:border-secondary-700 dark:bg-secondary-800"
            data-testid="coverage-summary"
          >
            <span className="font-medium text-secondary-900 dark:text-white">
              {matrix.summary.coveredCells} of {matrix.summary.totalCells} covered
            </span>
            <span className="inline-flex items-center gap-1 text-danger-600 dark:text-danger-400">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {matrix.summary.gapCells} coverage {matrix.summary.gapCells === 1 ? 'gap' : 'gaps'}
            </span>
          </div>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-secondary-50 dark:bg-secondary-900">
                    <th className="sticky left-0 z-10 border-b border-secondary-200 bg-secondary-50 p-3 text-left font-semibold text-secondary-700 dark:border-secondary-700 dark:bg-secondary-900 dark:text-secondary-200">
                      Security Domain
                    </th>
                    {matrix.regions.map((region) => (
                      <th
                        key={region}
                        className="border-b border-l border-secondary-200 p-3 text-left font-semibold text-secondary-700 dark:border-secondary-700 dark:text-secondary-200"
                      >
                        {REGION_LABELS[region] ?? region}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.domains.map((domain) => (
                    <tr key={domain} className="align-top">
                      <th
                        scope="row"
                        className="sticky left-0 z-10 border-b border-secondary-100 bg-white p-3 text-left font-medium text-secondary-800 dark:border-secondary-800 dark:bg-secondary-800 dark:text-secondary-100"
                      >
                        {DOMAIN_LABELS[domain] ?? domain}
                      </th>
                      {matrix.regions.map((region) => {
                        const cell = cellAt(matrix, region, domain);
                        const gap = !cell || cell.isGap;
                        const top = cell?.vendors[0];
                        return (
                          <td
                            key={`${region}-${domain}`}
                            data-testid={`cell-${region}-${domain}`}
                            className={cn(
                              'border-b border-l border-secondary-100 p-3 dark:border-secondary-800',
                              gap && 'bg-danger-50 dark:bg-danger-900/20',
                            )}
                          >
                            {gap || !top ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-danger-600 dark:text-danger-400">
                                <ExclamationTriangleIcon className="h-3.5 w-3.5" /> No vendor
                              </span>
                            ) : (
                              <div>
                                <div className="font-medium text-secondary-900 dark:text-white">{top.name}</div>
                                <div className="mt-0.5 flex items-center gap-2 text-xs text-secondary-500 dark:text-secondary-400">
                                  <span className="inline-flex items-center gap-0.5">
                                    <StarIcon className="h-3.5 w-3.5 text-amber-500" />
                                    {top.compositeRating.toFixed(1)}
                                  </span>
                                  {cell && cell.vendors.length > 1 && (
                                    <span>+{cell.vendors.length - 1} more</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
